import { AlertCircle, RefreshCw } from "lucide-react";
import type { JSX } from "react";

import { cn } from "../../lib/cn.js";
import { APIError } from "../../lib/http.js";
import { Button } from "../ui/button.js";

export interface ErrorStateProps {
  /** Pass an unknown error to derive title/detail from APIError or Error. */
  error?: unknown;
  /** Explicit title; overrides derived title from `error`. */
  title?: string;
  /** Explicit description; overrides derived detail from `error`. */
  description?: string;
  onRetry?: () => void;
  className?: string;
}

function readError(error: unknown): { title: string; detail?: string } {
  if (error instanceof APIError) {
    return { title: error.message, detail: error.displayDetail };
  }
  if (error instanceof Error) {
    return { title: error.message };
  }
  return { title: "Something went wrong" };
}

export function ErrorState({
  error,
  title: titleProp,
  description: descriptionProp,
  onRetry,
  className,
}: ErrorStateProps): JSX.Element {
  const derived = readError(error);
  const title = titleProp ?? derived.title;
  const detail = descriptionProp ?? derived.detail;
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 rounded-lg border border-border bg-card p-6 text-center",
        className,
      )}
      role="alert"
    >
      <AlertCircle className="h-8 w-8 text-destructive" aria-hidden="true" />
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {detail ? <p className="text-xs text-muted-foreground">{detail}</p> : null}
      </div>
      {onRetry ? (
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="mr-2 h-3 w-3" aria-hidden="true" />
          Try again
        </Button>
      ) : null}
    </div>
  );
}
